import { NgClass } from "@angular/common";
import { ChangeDetectorRef, Component, inject, Inject, OnInit, ViewChild } from "@angular/core";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatButton } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions } from "@angular/material/dialog";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthGamesService } from "@core/AuthServices/authGames/auth-games.service";
import { UserVersionService } from "@core/AuthServices/authUserVersion/auth-user-version.service";
import { LoadingService } from "@core/loading.service";
import { isMobile } from "@core/utility.service";
import { IonicModule, IonToggle } from "@ionic/angular";
import { TranslatePipe } from "@ngx-translate/core";
import { Firestore } from "@angular/fire/firestore";
import { doc, DocumentData, DocumentReference, onSnapshot } from "firebase/firestore";
import { interval, Observable, Subscription } from "rxjs";
import { CommonVariable } from "src/app/app.constants";
import { environment } from "src/environments/environment";
import { GamePlayComponent } from "src/app/components/game-play/game-play.component";
import { SpellFortuneComponent } from "src/app/components/spell-of-fortune/spell-of-fortune.component";
import { WindowsGamePlayPopupComponent } from "src/app/components/windows-game-play-popup/windows-game-play-popup.component";
import { GTMSagmentService } from "src/app/services/gtm-sagment.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { OrientationService } from "src/app/services/orientation.service";
import { GamesRepositoryService } from "../../games/games-repository.service";
import { GamesFeedSampleService } from "../games-feed-sample.service";
import { GameDatasService } from "../../games/services/game-data.service";
import { DevicePerformanceService } from "../../games/services/device-performance.service";
import { ClarityService } from "src/app/services/clarity.service";

interface DeviceInfo {
  batteryHealth: string;
  browserVersion: string;
  connectionType: string;
  deviceMemory: number;
  hardwareConcurrency: number;
  osVersion: string;
  userAgent: string;
}

@Component({
  selector: "app-game-preview",
  templateUrl: "./game-preview.component.html",
  styleUrls: ["./game-preview.component.scss"],
  standalone: true,
  imports: [
    MatDialogActions,
    MatButton,
    IonicModule,
    NgClass,
    MatProgressSpinner,
    TranslatePipe,
  ],
})
export class GamePreviewComponent implements OnInit {
  languageCode: string;
  spinnerAnimation: boolean;
  progressBar: number;
  timer$: Observable<number>;
  sub: Subscription;
  proceed = true;
  cardLoaded = false;
  prizeUnlockID: string;
  gameInfo$: Data;
  popupDetails: string;
  public preferredCurrency: string;
  gameDetails: Data;
  public isY2pPopup = false;
  discount_price = 25;
  @ViewChild('premiumToggle', { static: false }) premiumToggle!: IonToggle;
  private clarityService = inject(ClarityService);

  public gamesWithNoPrize: number[] = [];
  sessionData: DeviceInfo;
  firestoreData: Data;
  performanceGroupNumber: number;
  reason: string;
  public highPerformanceData: Data;
  public mediumPerformanceData: Data;
  public lowPerformanceData: Data;
  public veryLowPerformanceData: Data;
  public browserVersion: string;
  public osVersion: string;
  public numericalPart: string;
  public stringPart: string;
  public osName: string;
  public osNumber: string;
  gamePerformanceGroup: string;
  show: boolean;
  userGameRestriction: boolean;
  flashSaleEnabled: boolean;
  userSubscriptionStatus: string;
  flashSalesExpiryDate: Data;
  salesCost: number | string;
  private isPlayInProgress = false;
  premiumGamePromoDetails: Data;
  userPoints: number;
  private loadingService = inject(LoadingService);
  showPremiumToggle = false;
  constructor(
    private orientationService: OrientationService,
    private matDialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    @Inject(MAT_DIALOG_DATA) public gamePreviewData: Data,
    public gamesFeedService: GamesFeedSampleService,
    private authService: AuthService,
    private router: Router,
    private gameService: GamesRepositoryService,
    private changeDetectorRef: ChangeDetectorRef,
    private GTMSegment: GTMSagmentService,
    private GTMSegmentService: GTMSegmentService,
    private authGamesService: AuthGamesService,
    private UserVersionService: UserVersionService,
    private gameDatasService: GameDatasService,
    private devicePerformanceService: DevicePerformanceService,
    private firestore: Firestore
  ) {
    this.authService.captureDeviceInfo();
    this.languageCode = CommonVariable.preferredLanguage;
    this.gamesFeedService.isGamePopupVisible = true;
    this.authService?.userDetails$?.subscribe((user: Data) => {
      this.languageCode = user?.language_code ?? CommonVariable.preferredLanguage;
      this.preferredCurrency = user?.preferred_currency ? user?.preferred_currency : CommonVariable.preferredCurrency;
      this.userGameRestriction = user?.gameplay_restriction || false;
      this.userSubscriptionStatus = user?.pwa_segment_status || "";
    });
    this.gamesFeedService.getUserCurrentBalance().subscribe((balance: Data) => {
      this.userPoints = balance?.data?.points || 0;
    });
  }

  ngOnInit(): void {
    this.showPremiumToggle = this.gamesFeedService.shouldShowPremiumToggle(this.gamePreviewData);
    /* -------------------------------------------------------
     * Flash Sale / Pricing Logic
     * -----------------------------------------------------*/
    const flashSaleDetails = this.gamePreviewData?.sale_details;
    const saleDetails = this.gameDatasService?.getFlashSaleData(flashSaleDetails);
    this.flashSalesExpiryDate = saleDetails?.flash_sale_date;
    this.salesCost = this.authService?.flashSaleEnabled ? saleDetails?.sales_cost : "";
    this.flashSaleEnabled = this.gameDatasService?.isFlashSaleToday(this.flashSalesExpiryDate);
    // GTM Event
    this.GTMSegmentService?.GTMPopupInitializationForInboxNew("Game Launcher Viewed", {
      Game_Name: this.gamePreviewData?.game_title,
      Dwell_Time: '0.1s',
    });

    /* -------------------------------------------------------
     * Performance Group Logic
     * -----------------------------------------------------*/
    this.gamePerformanceGroup = this.gamePreviewData?.performance_group;
    this.sessionData = this.devicePerformanceService.getSessionData();
    if (
      this.sessionData &&
      this.sessionData.browserVersion !== "N/A" &&
      this.sessionData.osVersion !== "N/A" &&
      this.sessionData.batteryHealth !== "N/A" &&
      this.sessionData.connectionType !== "N/A"
    ) {
      this.browserVersion = this.sessionData.browserVersion;
      const match = this.browserVersion.match(/([a-zA-Z]+) ([\d.]+)/);
      this.numericalPart = match?.[2];
      this.stringPart = match?.[1];
      this.osVersion = this.sessionData.osVersion;
      const match2 = this.osVersion.match(/([a-zA-Z]+) ([\d.]+)/);
      this.osName = match2?.[1];
      this.osNumber = match2?.[2];
      const partsData = {
        stringPart: this.stringPart,
        numericalPart: this.numericalPart,
        OSNum: this.osNumber,
        osName: this.osName,
      };
      this.UserVersionService.getPerformanceGroupRequirements().subscribe((data) => {
        this.firestoreData = data;
        this.highPerformanceData = this.firestoreData.High_Performance;
        this.mediumPerformanceData = this.firestoreData.Medium_Performance;
        this.lowPerformanceData = this.firestoreData.Low_Performance;
        this.veryLowPerformanceData = this.firestoreData.Very_Low_Performance;
        let dataResult: any;
        switch (this.gamePerformanceGroup) {
          case "high":
            dataResult = this.devicePerformanceService.highPerformance(this.highPerformanceData, this.sessionData, partsData);
            break;

          case "medium":
            dataResult = this.devicePerformanceService.mediumPerformance(
              this.highPerformanceData,
              this.mediumPerformanceData,
              this.sessionData,
              partsData
            );
            break;

          case "low":
            dataResult = this.devicePerformanceService.lowPerformance(
              this.highPerformanceData,
              this.mediumPerformanceData,
              this.lowPerformanceData,
              this.sessionData,
              partsData
            );
            break;

          case "very low":
            dataResult = this.devicePerformanceService.veryLowPerformance(
              this.highPerformanceData,
              this.mediumPerformanceData,
              this.lowPerformanceData,
              this.veryLowPerformanceData,
              this.sessionData,
              partsData
            );
            break;

          default:
            console.warn("[ngOnInit] ⚠ Unknown performance group:", this.gamePerformanceGroup);
        }
        if (dataResult?.PerformanceGroupNumber === 0) {
          this.reason = dataResult.Reason;
          this.show = true;
        } else {
          this.show = false;
        }
      });
    }

    /* -------------------------------------------------------
     * Top Prize Formatting Logic
     * -----------------------------------------------------*/
    this.checkImageLoad();

    this.gameDetails = this.gamePreviewData?.int_game_popup_details
      ? this.gamePreviewData.int_game_popup_details[this.languageCode]
      : this.gamePreviewData?.game_popup_details;
    let topPrize = this.gamePreviewData?.int_top_prize
      ? this.gamePreviewData.int_top_prize[this.languageCode]
      : this.gamePreviewData?.top_prize;
    if (topPrize) {
      topPrize = topPrize.replace(/ Cash| Bargeld| Efectivo| Espèces| Contanti/, "");
      topPrize = topPrize.replace(/ Credito| Crédito| Crédit| Credit/, "Cr");
      topPrize = topPrize.replace(/ Kredit/, "Kr");
      this.authService
        .getDVFromString(topPrize)
        .then((resp: string) => {
          this.gamePreviewData.top_prize = resp;
        })
        .catch((err) => {
          console.error("[ngOnInit] ❌ getDVFromString (topPrize) failed:", err);
        });
    }

    // Extract prize tier
    let start = topPrize?.indexOf("[currency_beautify:");
    let end = topPrize?.indexOf("]", start);
    let dvToReplace = topPrize?.substring(start, end + 1);
    dvToReplace = dvToReplace?.replace(",", "");
    let formatedDVData = dvToReplace?.replace("[", "")?.replace("]", "");
    let result = formatedDVData?.split(":");
    let prize = Number(result?.[1]);
    if (prize < 100) this.gamePreviewData.tier = 1;
    else if (prize >= 100 && prize <= 1000) this.gamePreviewData.tier = 2;
    else this.gamePreviewData.tier = 3;
    /* -------------------------------------------------------
     * Popup Details (DV)
     * -----------------------------------------------------*/
    if (this.gamePreviewData?.int_game_popup_details) {
      this.authService
        ?.getDVFromString(this.gamePreviewData.int_game_popup_details[this.languageCode])
        .then((resp: string) => {
          this.popupDetails = resp;
        })
        .catch((err) => console.error("[ngOnInit] ❌ DV parse failed:", err));
    } else {
      const cc = this.gamePreviewData?.cc ?? 44;
      const gamePopupDetails =
        this.preferredCurrency == CommonVariable.CAD
          ? this.gamePreviewData?.country_code_data?.[1]?.game_popup_details
          : this.preferredCurrency == CommonVariable.EUR
            ? this.gamePreviewData?.country_code_data?.[353]?.game_popup_details
            : this.gamePreviewData?.country_code_data?.[cc]?.game_popup_details;
      this.authService
        ?.getDVFromString(gamePopupDetails)
        ?.then((resp: string) => {
          this.popupDetails = resp;
        })
        .catch((err) => console.error("[ngOnInit] ❌ DV parse failed:", err));
    }

    /* -------------------------------------------------------
     * Y2P Prize Unlock Subscription
     * -----------------------------------------------------*/
    this.gamesFeedService?.prizeUnlock$?.subscribe((data) => {
      this.prizeUnlockID = data;
      if (this.prizeUnlockID == this.gamePreviewData?.game_id) {
        this.gamesFeedService
          ?.getGameInfoY2P(this.gamePreviewData.game_id, "yours_to_play")
          ?.subscribe((data) => {
            this.gameInfo$ = data;
          });
      }
    });

    /* -------------------------------------------------------
     * Y2P Popup Status
     * -----------------------------------------------------*/
    this.gamesFeedService.getY2pPopupStatus().subscribe(
      (value: boolean) => {
        this.isY2pPopup = value;
      },
      (err) => console.error("[ngOnInit] ❌ getY2pPopupStatus Error:", err)
    );

    /* -------------------------------------------------------
     * Premium Promo Details
     * -----------------------------------------------------*/
    this.gamesWithNoPrize = [...this.gameService.gamesWithNoPrize];
    this.gamesFeedService?.getPremiumGamePromoDetails()?.subscribe((data) => {
      if (data) this.premiumGamePromoDetails = data;
    });
  }

  openPremiumGamePopup(gamePath: string) {
    if (!gamePath || typeof gamePath !== 'string') {
      this.authService.errorHandlerLogs("[openGamePreview] premium_game_id is missing or not a string", {gamePath});
      return;
    }

    const trimmedPath = gamePath.trim();
    if (!trimmedPath) {
      this.authService.errorHandlerLogs("[openGamePreview] premium_game_id is empty after trimming", {gamePath});
      return;
    }

    const normalizedPath = this.normalizeFirestoreGamePath(trimmedPath);
    if (!normalizedPath) {
      this.authService.errorHandlerLogs("[openPremiumGamePopup] normalized path is invalid", { trimmedPath });
      return;
    }
    let gameRef: DocumentReference<DocumentData>;
    try {
      gameRef = doc(this.firestore, normalizedPath);
    } catch (err) {
      this.authService.errorHandlerLogs('[openGamePreview] Invalid Firestore document path:', { trimmedPath, err });
      return;
    }

    this.matDialog.closeAll();

    onSnapshot(gameRef, (ref) => {
      if (!ref.exists()) {
        console.warn('[openGamePreview] Game document does not exist.');
        return;
      }

      const gameData = ref.data();
      const inputData = { myString: gameData, currency: this.preferredCurrency };
      this.loadingService.present();
      this.authService.getDvResolvedJson(inputData).then((gameDataDvResolved: Data) => {

        // this.gamesRepoService?.setCurrentGameOrientationStatus(gameData?.orientation);
        this.loadingService.dismiss();
        const gamePrice =
          (this.userSubscriptionStatus === "subscriber" && gameDataDvResolved?.data?.subscriber_price)
            ? gameDataDvResolved.data.subscriber_price
            : gameDataDvResolved?.data?.game_price;
        gameDataDvResolved.data["game_price"] = gamePrice;
        const dialogRef = this.matDialog.open(GamePreviewComponent, {
          panelClass: "gamePreview-size",
          backdropClass: "ongoingWarning-bg-back-drop-color",
          data: gameDataDvResolved?.data, // ✅ new premium data
        });

        dialogRef.afterClosed().subscribe(() => { });

      }).catch((err) => {
        console.error('[openGamePreview] Error resolving game data from snapshot:', err);
        this.loadingService.dismiss();

        this.authService.getConstantsMsg("1013").then((translatedMsg) => {
          const defaultMsg = {
            message: { line_1: "Something went wrong (user)", line_2: "" },
            style: { icon: "tick circle error.svg", type: CommonVariable.error },
          };
          this.authService.showToastMessage(translatedMsg, defaultMsg);
        });
      });

    });
  }

  goToGameDetails(gameTypeId: number, gameId: number, category: string, flashCost: Data) {
    if (!category) {
      this.authService.getConstantsMsg("1026")
        .then((translatedMsg) => {
          const defaultMsg = {
            message: {
              line_1: "Something went wrong. Please try again",
              line_2: "",
            },
            style: {
              icon: "tick circle error.svg",
              type: "Error",
            },
          };
          this.authService.showToastMessage(translatedMsg, defaultMsg);
        })
        .catch((error) => {
          return error;
        });
      return;
    }
    const gameDetailUrl = category.toLowerCase().replace(" ", "-");
    this.matDialog.closeAll();
    this.gamesFeedService.isGamePopupVisible = false;
    if (gameTypeId !== 10) {
      this.router.navigate([`games/${gameDetailUrl}-game`, gameId], {
        state: flashCost
      });
    } else if (gameTypeId === 10) {
      this.router.navigate([`games/replayable`, gameId], {
        state: flashCost
      });
    }
  }

  openPremiumGamesPromo(gamePrice: number) {
    this.premiumGamePromoDetails["game_price"] = gamePrice;
    this.bottomSheet.open(SpellFortuneComponent, {
      panelClass: 'inbox-main-wrapper',
      backdropClass: "ongoingWarning-back-drop-color",
      data: this.premiumGamePromoDetails,
    });
  }

  closeDialog() {
    this.matDialog.closeAll();
    this.gamesFeedService.isGamePopupVisible = false;
  }

  startSpinner() {
    this.spinnerAnimation = true;
    this.progressBar = 0;
    this.timer$ = interval(20);
    this.sub = this.timer$.subscribe(
      (sec) => {
        this.progressBar = 0 + sec;
        this.changeDetectorRef.detectChanges();
      },
      (err) => {
        return err;
      }
    );
  }

 async playGame() {
    if (!this.gamePreviewData) return;
    this.clarityService.event("play_game");
    const isPremiumToggled = this.premiumToggle?.checked || false;
    const gamePrice =  this.gamePreviewData?.sale_price || this.gamePreviewData?.game_price;
      if (this.gamePreviewData?.category === "Prize Box") {
        //This method is used to open the prize box
        this.gameService.OpenPrizeBox(this.gamePreviewData);
      } else {
        let startTime = new Date();
        this.togglePopupDisabled();
        this.GTMSegment?.HotJarEvent("user_played_game");
        if (!isMobile.any() && this.gamePreviewData?.orientation == "landscape") {
          this.togglePopupDisabled();
          this.matDialog.open(WindowsGamePlayPopupComponent);
          return null;
        }
        if (!window.navigator.onLine) return;
        this.startSpinner();
        this.gamePreviewData.document = "/api_post_request/customer_game_token_authentication";
        this.gamePreviewData.from_page = "game-preview";
        this.gamePreviewData.user_game_restriction = this.isY2pPopup ? false : this.userGameRestriction;
        if (this.isPlayInProgress) return;
        this.isPlayInProgress = true;
        this.gamesFeedService
          .getUserGameInfo(this.gamePreviewData)
          .then((data: Data) => {
            this.isPlayInProgress = false;
            if(data && (data?.play_type === "free" || data?.play_type === "gifted" || (!this.userGameRestriction || this.isY2pPopup) || this.gamePreviewData?.category === "Prize Unlocker")) {
              let endTime = new Date();
              let dwellTime = endTime.getTime() - startTime.getTime();
              const body = {
                Game_Name: this.gamePreviewData?.game_title,
                CTA_Text: "Play Now",
                Destination_URL: data?.play_url,
                Dwell_Time: dwellTime,
                Game_Type: this.gamePreviewData?.gamer_type,
              };
              this.GTMSegmentService?.GTMPopupInitializationForInboxNew("Game Launcher Clicked ", body);
              this.progressBar === 100;
              this.sub?.unsubscribe();

              setTimeout(() => {
                this.spinnerAnimation = false;
                this.proceed = false;
                setTimeout(() => {
                  this.proceed = true;
                }, 2000);
              }, 300);

              this.togglePopupDisabled();
              if ((isPremiumToggled || this.gamePreviewData?.is_premium_game) && data?.game_price && data?.game_price > this.userPoints) {
                const gamePrice = (this.userSubscriptionStatus === "subscriber" && this.gamePreviewData?.subscriber_price)
                  ? this.gamePreviewData?.subscriber_price
                  : data?.game_price;
                this.openPremiumGamesPromo(gamePrice);
                return;
              } else {
                this.openPlayGame(data);
              }
            } else {
              this.togglePopupDisabled();
              this.matDialog.closeAll();
              this.gamesFeedService.isGamePopupVisible = false;
              this.authGamesService?.liftRestrictionInboxMessage(gamePrice);
              this.authService.getConstantsMsg("1148")
              .then((translatedMsg) => {
                const message = "You have reached the game play limit";
                const defaultMsg = {
                  message: {
                    line_1: message,
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              })
              .catch((error) => {
                return error;
              });
            }
          })
          .catch((data) => {
            this.isPlayInProgress = false;
            // this.snackService.openSnackBar(data.message, "", "Error", "Error");
            //this.authService.showToast(data.message ? data.message : "Code Error", "Error", "");
            try {
              this.togglePopupDisabled();
              const translatedMsg = {
                message: {
                  line_1: data?.message,
                  line_2: "",
                },
                style: {
                  duration: 2000,
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              };
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong!",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
              return data;
            } catch (e) {
              this.togglePopupDisabled();
              return e;
            }
          });
      }
    
    this.matDialog.closeAll();
    this.gamesFeedService.isGamePopupVisible = false;
  }

  private async openPlayGame(res: Data) {
    if (isMobile.Android()) {
      this.orientationService.enterFullscreen();
      this.orientationService.lockOrientation(this.gamePreviewData?.orientation);
    }
    this.matDialog.open(GamePlayComponent, {
      height: "100%",
      width: "100%",
      data: { data: res },
    });
  }

  togglePopupDisabled() {
    const currentVal = this.gamesFeedService?.popupDisabled$.getValue();
    this.gamesFeedService?.popupDisabled$.next(!currentVal);
  }

  // To avoid showing image loading partially on popup
  checkImageLoad() {
    const backgroundImage = new Image();
    backgroundImage.src = this.gamePreviewData?.game_popup_image_url;
    backgroundImage.onload = () => {
      this.cardLoaded = true;
    };

    backgroundImage.onerror = () => {
      this.cardLoaded = true;
    };
  }

  dialogClose() {
    this.show = false;
  }
  onToggleChange(event: Data) {
    const id = event.detail.checked
      ? this.gamePreviewData?.premium_game_id
      : this.gamePreviewData?.parent_game_id;
    if (typeof id !== 'string') {
      this.authService.errorHandlerLogs("[onToggleChange] game id is not a string", {id});
      return;
    }
    this.openPremiumGamePopup(id);
  }

  private normalizeFirestoreGamePath(gamePath: string): string {
    let normalized = gamePath.trim();
    if (normalized.startsWith('/')) {
      normalized = normalized.slice(1);
    }

    const gameRoot = 'general_modules/game/';
    if (normalized.startsWith(gameRoot)) {
      const rest = normalized.slice(gameRoot.length);
      if (!rest.startsWith(environment.mirrorPath)) {
        normalized = `${gameRoot}${environment.mirrorPath}${rest}`;
      }
    }

    return normalized;
  }

}
